import { useSortable } from "@dnd-kit/sortable";
import { GripVertical } from "lucide-react";
import * as React from "react";

export interface DraggableRowHandleProps {
  /** Unique ID for the row (dnd-kit mode) */
  rowId?: string;
  /** Legacy: item ID (HTML5 DnD mode) */
  itemId?: string;
  /** Legacy: drag start handler (HTML5 DnD mode) */
  onDragStart?: (e: React.DragEvent) => void;
  /** Legacy: drag end handler (HTML5 DnD mode) */
  onDragEnd?: () => void;
  /** Legacy: is dragging state (HTML5 DnD mode) */
  isDragging?: boolean;
  className?: string;
  disabled?: boolean;
  size?: "sm" | "md" | "lg";
  "aria-label"?: string;
}

/**
 * Drag handle for table rows.
 *
 * **dnd-kit mode (new)**: Pass `rowId`. Must be used inside a SortableContext.
 * **HTML5 DnD mode (legacy)**: Pass `itemId`, `onDragStart`, `onDragEnd`, `isDragging`.
 */
export function DraggableRowHandle({
  rowId,
  itemId,
  onDragStart,
  onDragEnd,
  isDragging = false,
  className = "",
  disabled = false,
  size = "sm",
  "aria-label": ariaLabel = "Drag to reorder",
}: DraggableRowHandleProps) {
  // Determine mode: dnd-kit (rowId) or HTML5 DnD (itemId + callbacks)
  const shouldUseDndKit = rowId !== undefined;
  const sortable = useSortable({ id: rowId ?? "" });
  const attributes = shouldUseDndKit ? sortable.attributes : undefined;
  const listeners = shouldUseDndKit ? sortable.listeners : undefined;

  const sizeClasses = {
    sm: "h-4 w-4",
    md: "h-5 w-5",
    lg: "h-6 w-6",
  };

  // HTML5 DnD handlers (legacy mode)
  const handleDragStart = (e: React.DragEvent) => {
    if (!onDragStart || !itemId) return;
    e.dataTransfer.effectAllowed = "move";
    e.dataTransfer.setData("text/plain", itemId);

    // Hide the default drag image
    const emptyImg = new Image();
    emptyImg.src = "data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7";
    e.dataTransfer.setDragImage(emptyImg, 0, 0);

    onDragStart(e);
  };

  if (disabled) {
    return (
      <div className={`flex justify-center ${className}`}>
        <div className="p-1 opacity-50">
          <GripVertical className={`${sizeClasses[size]} text-muted-foreground`} />
        </div>
      </div>
    );
  }

  return (
    <div className={`flex justify-center ${className}`}>
      <button
        {...attributes}
        {...listeners}
        draggable={!shouldUseDndKit}
        onDragStart={!shouldUseDndKit ? handleDragStart : undefined}
        onDragEnd={!shouldUseDndKit ? onDragEnd : undefined}
        className={`
          cursor-grab active:cursor-grabbing p-1 hover:bg-muted/50 rounded transition-colors
          ${!shouldUseDndKit && isDragging ? "opacity-50" : ""}
        `}
        aria-label={ariaLabel}
        disabled={disabled}
      >
        <GripVertical className={`${sizeClasses[size]} text-muted-foreground`} />
      </button>
    </div>
  );
}
